import { useEffect, useState } from 'react';

import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';
import AllPlans from './AllPlans';

// Report reasons as the reporter picked them (scam_fraud and misleading added 2026-10-08).
const REASON_LABELS = {
  scam_fraud: 'Scam or fraud',
  misleading: 'Fake or misleading',
  safety: 'Safety concern',
  harassment: 'Harassment or hate',
  inappropriate: 'Sexual or inappropriate',
  solicitation: 'Spam or selling',
  no_show: 'No-show',
  other: 'Something else',
};

// Moderation inbox. Two surfaces share one queue (`surface` on the report):
//   - Plans: a member reported someone's plan. Remedy is taking the PLAN down.
//   - Tables: a member reported another member. Remedy is banning the USER.
// Two jobs:
//   1. Reports inbox — every open report, filtered by surface.
//   2. Reviews viewer — Google-style page of who left what review for a user
//      (author, stars, showed/no-show, note). Opens from a report or a lookup.
// Backend: /table-invites/admin/* (see zona-brain/specs/table-invites-safety-layer.md).

const card = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 16 };
const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' });
const chip = (bg, fg) => ({ background: bg, color: fg, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 });

const fmt = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
};

const Avatar = ({ p, size = 40 }) =>
  p?.photo ? (
    <img src={p.photo} alt={p.name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />
  ) : (
    <div style={{ width: size, height: size, borderRadius: '50%', background: 'linear-gradient(135deg,#7229F5,#4F30EA)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff' }}>
      {(p?.name || 'Z')[0]}
    </div>
  );

// The reported plan itself. The event is what is under review, so it leads
// the card; who posted it is secondary.
const PlanRow = ({ plan }) => {
  if (!plan) {
    return (
      <div style={{ color: 'rgba(255,255,255,.35)', fontSize: 13, fontStyle: 'italic', margin: '12px 0' }}>
        This plan was deleted after the report was filed.
      </div>
    );
  }
  const when = plan.date
    ? new Date(plan.date).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : '';
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '12px 0', background: 'rgba(0,0,0,.22)', padding: 12, borderRadius: 12 }}>
      {plan.image_url ? (
        <img src={plan.image_url} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover', flex: 'none' }} />
      ) : (
        <div style={{ width: 56, height: 56, borderRadius: 10, flex: 'none', background: 'rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,.3)', fontSize: 11 }}>
          no flyer
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <div style={{ color: '#fff', fontSize: 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {plan.name || 'Untitled plan'}
        </div>
        <div style={{ color: 'rgba(255,255,255,.5)', fontSize: 12, marginTop: 2 }}>
          {[plan.venue_name, when].filter(Boolean).join(' · ')}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          {plan.status !== 'active' && <span style={chip('rgba(255,110,110,0.14)', '#ff8d8d')}>{plan.status}</span>}
          {plan.is_private && <span style={chip('rgba(255,255,255,0.08)', 'rgba(255,255,255,.6)')}>approval required</span>}
        </div>
      </div>
    </div>
  );
};

const Stars = ({ n }) => (
  <span style={{ color: '#ffc94d', fontSize: 14, letterSpacing: 1 }}>
    {'★'.repeat(n || 0)}<span style={{ color: 'rgba(255,255,255,.2)' }}>{'★'.repeat(Math.max(0, 5 - (n || 0)))}</span>
  </span>
);

const TableModeration = () => {
  // Plans first: it is the surface that opens to every member.
  const [surface, setSurface] = useState('user_guest_list');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [reviewsFor, setReviewsFor] = useState(null); // { user, data }
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const loadReports = async () => {
    if (surface === 'all_plans') return;
    setLoading(true);
    try {
      const res = await api.get(`/table-invites/admin/reports?status=open&surface=${surface}`);
      setReports(Array.isArray(res.data?.reports) ? res.data.reports : []);
    } catch (e) {
      console.log('load reports error', e?.response?.status);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReports(); }, [surface]);

  const action = async (report, act) => {
    setBusyId(report._id);
    try {
      await api.post(`/table-invites/admin/reports/${report._id}/action`, { action: act });
      setReports((prev) => prev.filter((r) => r._id !== report._id));
    } catch (e) {
      alert('Could not update the report. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  const openReviews = async (user) => {
    if (!user) return;
    setReviewsFor({ user, data: null });
    setReviewsLoading(true);
    try {
      const res = await api.get(`/table-invites/admin/users/${user.user_id}/reviews`);
      setReviewsFor({ user, data: res.data });
    } catch (e) {
      setReviewsFor({ user, data: { summary: { count: 0 }, reviews: [] } });
    } finally {
      setReviewsLoading(false);
    }
  };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section style={{ padding: '20px 2vw' }}>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>Moderation</h1>
            <p style={{ color: 'rgba(255,255,255,.45)', fontSize: 13, margin: '0 0 16px' }}>
              {surface === 'all_plans'
                ? 'Every plan members have posted'
                : loading ? 'Loading…' : `${reports.length} open report${reports.length === 1 ? '' : 's'}`}
            </p>

            <div style={{ display: 'flex', gap: 8, margin: '0 0 22px' }}>
              {[
                { id: 'user_guest_list', label: 'Reported plans' },
                { id: 'all_plans', label: 'All plans' },
                { id: 'table_invite', label: 'Reported people' },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setSurface(t.id)}
                  style={{
                    background: surface === t.id ? 'rgba(79,48,234,0.85)' : 'rgba(255,255,255,0.05)',
                    color: surface === t.id ? '#fff' : 'rgba(255,255,255,.6)',
                    border: '1px solid ' + (surface === t.id ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.08)'),
                    borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}>
                  {t.label}
                </button>
              ))}
            </div>

            {surface === 'all_plans' && <AllPlans />}

            {surface !== 'all_plans' && !loading && reports.length === 0 && (
              <div style={{ ...card, padding: 40, textAlign: 'center', color: 'rgba(255,255,255,.4)' }}>
                {surface === 'user_guest_list'
                  ? 'No reported plans. A member reporting a plan lands it here immediately.'
                  : 'No open reports. Reports from a table land here the moment a user files one.'}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 760 }}>
              {(surface === 'all_plans' ? [] : reports).map((r) => (
                <div key={r._id} style={card}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span style={chip('rgba(255,110,110,0.14)', '#ff8d8d')}>{REASON_LABELS[r.reason] || r.reason}</span>
                    <span style={{ color: 'rgba(255,255,255,.4)', fontSize: 12 }}>{fmt(r.created_at)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '12px 0', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Avatar p={r.reporter} size={34} />
                      <div><div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)' }}>Reporter</div><div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{r.reporter?.name || r.reporter_user_id}</div></div>
                    </div>
                    <span style={{ color: 'rgba(255,255,255,.3)' }}>→</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Avatar p={r.reported} size={34} />
                      <div><div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)' }}>{r.surface === 'user_guest_list' ? 'Host' : 'Reported'}</div><div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{r.reported?.name || r.reported_user_id}</div></div>
                    </div>
                  </div>
                  {r.surface === 'user_guest_list' && <PlanRow plan={r.plan} />}
                  {r.details && <p style={{ color: 'rgba(255,255,255,.7)', fontSize: 13, lineHeight: 1.5, margin: '0 0 12px', background: 'rgba(0,0,0,.2)', padding: 12, borderRadius: 10 }}>“{r.details}”</p>}
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {r.surface === 'user_guest_list' ? (
                      <button
                        disabled={busyId === r._id || !r.plan || r.plan.status !== 'active'}
                        style={btn('#c0392b')}
                        onClick={() => { if (window.confirm(`Take down “${r.plan?.name || 'this plan'}”? It drops off the row, the map and search. You can put it back.`)) action(r, 'takedown'); }}>
                        {r.plan && r.plan.status !== 'active' ? 'Already down' : 'Take down'}
                      </button>
                    ) : (
                      <button disabled={busyId === r._id} style={btn('#c0392b')} onClick={() => { if (window.confirm(`Ban ${r.reported?.name || 'this user'} from table invites?`)) action(r, 'ban'); }}>Ban user</button>
                    )}
                    <button disabled={busyId === r._id} style={btn('rgba(255,255,255,0.1)')} onClick={() => action(r, 'dismiss')}>Dismiss</button>
                    {r.reported && <button style={btn('rgba(79,48,234,0.5)')} onClick={() => openReviews(r.reported)}>View reviews</button>}
                  </div>
                </div>
              ))}
            </div>

            {reviewsFor && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'flex-end', zIndex: 60 }} onClick={() => setReviewsFor(null)}>
                <div style={{ width: 'min(480px, 92vw)', height: '100%', background: '#0d0d12', borderLeft: '1px solid rgba(255,255,255,0.1)', padding: 24, overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
                    <Avatar p={reviewsFor.user} size={48} />
                    <div>
                      <div style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>{reviewsFor.user.name}</div>
                      {reviewsFor.data?.summary?.count > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                          <Stars n={Math.round(reviewsFor.data.summary.avg_stars)} />
                          <span style={{ color: 'rgba(255,255,255,.6)', fontSize: 13 }}>{reviewsFor.data.summary.avg_stars} · {reviewsFor.data.summary.count} review{reviewsFor.data.summary.count === 1 ? '' : 's'}</span>
                          {reviewsFor.data.summary.no_shows > 0 && <span style={chip('rgba(255,180,80,0.14)', '#ffc37d')}>{reviewsFor.data.summary.no_shows} no-show</span>}
                        </div>
                      )}
                    </div>
                    <button style={{ marginLeft: 'auto', ...btn('rgba(255,255,255,0.1)') }} onClick={() => setReviewsFor(null)}>Close</button>
                  </div>

                  {reviewsLoading && <p style={{ color: 'rgba(255,255,255,.4)' }}>Loading…</p>}
                  {!reviewsLoading && (reviewsFor.data?.reviews?.length ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {reviewsFor.data.reviews.map((rev) => (
                        <div key={rev._id} style={card}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                            <Avatar p={rev.author} size={30} />
                            <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{rev.author?.name || 'ZONA member'}</div>
                            <span style={{ marginLeft: 'auto', color: 'rgba(255,255,255,.35)', fontSize: 11 }}>{fmt(rev.created_at)}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <Stars n={rev.stars} />
                            {rev.showed === false && <span style={chip('rgba(255,180,80,0.14)', '#ffc37d')}>No-show</span>}
                            {rev.respectful === false && <span style={chip('rgba(255,110,110,0.14)', '#ff8d8d')}>Flagged</span>}
                          </div>
                          {rev.note && <p style={{ color: 'rgba(255,255,255,.75)', fontSize: 13, lineHeight: 1.5, margin: 0 }}>{rev.note}</p>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: 'rgba(255,255,255,.4)', fontSize: 13 }}>No reviews yet for this user.</p>
                  ))}
                </div>
              </div>
            )}
          </section>
        </article>
      </div>
    </main>
  );
};

export default TableModeration;
